import ExcelJS from 'exceljs';
import { MatrixLikelihoodType, MatrixRiskType, MatrixSeverityType } from '../../../types/types';

type ExportMatrixParams = {
  matrixName?: string;
  severityLevels: MatrixSeverityType[];
  likelihoodLevels: MatrixLikelihoodType[];
  riskLevels: MatrixRiskType[];
  inverse?: boolean;
};

const DEFAULT_CELL_COLOR = 'FFFFFF';
const DEFAULT_FONT_COLOR = '000000';
const DEFAULT_BORDER_COLOR = 'CCCCCC';
const SEVERITY_HEADER_COLOR = '85B7E6FF';
const SEVERITY_FONT_COLOR = '385079FF';
const LIKELIHOOD_HEADER_COLOR = 'BCC599FF';
const LIKELIHOOD_FONT_COLOR = '4A5D23FF';
// Excel row heights are in points; 100px ≈ 75pt
const ROW_HEIGHT_PT = 75;

const sanitizeFileName = (value?: string) => {
  if (!value) return 'matriz';
  return value.replace(/[^\w.-]+/g, '_');
};

const normalizeHexColor = (value?: string | null, fallback = ''): string => {
  if (!value) return fallback;
  const cleaned = value.replace(/#/g, '').trim().toUpperCase();

  if (/^[0-9A-F]{8}$/.test(cleaned)) {
    const [r, g, b, a] = cleaned.match(/.{2}/g) ?? [];
    if (r && g && b && a) {
      return `${a}${r}${g}${b}`; // Convert from RGBA to ARGB expected by Excel
    }
    return cleaned;
  }

  if (/^[0-9A-F]{6}$/.test(cleaned)) return `FF${cleaned}`;

  if (/^[0-9A-F]{3}$/.test(cleaned)) {
    const expanded = cleaned
      .split('')
      .map((char) => `${char}${char}`)
      .join('');
    return `FF${expanded}`;
  }

  return fallback;
};

const buildRiskKey = (severity: MatrixSeverityType, likelihood: MatrixLikelihoodType) =>
  `${severity.id ?? severity.name}-${likelihood.id ?? likelihood.name}`;

const buildBorder = (color = DEFAULT_BORDER_COLOR): Partial<ExcelJS.Borders> => {
  const style: ExcelJS.BorderStyle = 'thin';
  return {
    top: { style, color: { argb: color } },
    left: { style, color: { argb: color } },
    bottom: { style, color: { argb: color } },
    right: { style, color: { argb: color } },
  };
};

export const exportRiskMatrixToXlsx = async ({
  matrixName,
  severityLevels,
  likelihoodLevels,
  riskLevels,
  inverse,
}: ExportMatrixParams) => {
  const workbook = new ExcelJS.Workbook();
  const sheet = workbook.addWorksheet('Matriz de riesgo');

  const rowHeaders = inverse ? severityLevels : likelihoodLevels;
  const columnHeaders = inverse ? likelihoodLevels : severityLevels;

  const headerFillColor = inverse ? LIKELIHOOD_HEADER_COLOR : SEVERITY_HEADER_COLOR;
  const headerFontColor = inverse ? LIKELIHOOD_FONT_COLOR : SEVERITY_FONT_COLOR;
  const rowHeaderFillColor = inverse ? SEVERITY_HEADER_COLOR : LIKELIHOOD_HEADER_COLOR;
  const rowHeaderFontColor = inverse ? SEVERITY_FONT_COLOR : LIKELIHOOD_FONT_COLOR;

  sheet.views = [{ state: 'frozen', xSplit: 1, ySplit: 1 }];
  sheet.columns = [
    { width: 24 },
    ...columnHeaders.map(() => ({ width: 18 })),
  ];
  sheet.getRow(1).height = ROW_HEIGHT_PT;

  const riskMap = new Map<string, MatrixRiskType>();
  riskLevels.forEach((risk) => {
    if (risk.severity && risk.likelihood) {
      riskMap.set(buildRiskKey(risk.severity, risk.likelihood), risk);
    }
  });

  // Header row
  columnHeaders.forEach((header, index) => {
    const cell = sheet.getCell(1, index + 2);
    cell.value = header.name;
    cell.fill = {
      type: 'pattern',
      pattern: 'solid',
      fgColor: { argb: normalizeHexColor(headerFillColor, `FF${DEFAULT_CELL_COLOR}`) },
    };
    cell.font = { bold: true, color: { argb: normalizeHexColor(headerFontColor, `FF${DEFAULT_FONT_COLOR}`) } };
    cell.alignment = { vertical: 'middle', horizontal: 'center', wrapText: true };
    cell.border = buildBorder();
  });

  // Rows with headers and values
  rowHeaders.forEach((rowItem, rowIndex) => {
    const excelRow = rowIndex + 2;
    sheet.getRow(excelRow).height = ROW_HEIGHT_PT;
    const headerCell = sheet.getCell(excelRow, 1);
    headerCell.value = rowItem.name;
    headerCell.fill = {
      type: 'pattern',
      pattern: 'solid',
      fgColor: { argb: normalizeHexColor(rowHeaderFillColor, `FF${DEFAULT_CELL_COLOR}`) },
    };
    headerCell.font = { bold: true, color: { argb: normalizeHexColor(rowHeaderFontColor, `FF${DEFAULT_FONT_COLOR}`) } };
    headerCell.alignment = { vertical: 'middle', horizontal: 'center', wrapText: true };
    headerCell.border = buildBorder();

    columnHeaders.forEach((columnItem, colIndex) => {
      const severity = inverse ? (rowItem as MatrixSeverityType) : (columnItem as MatrixSeverityType);
      const likelihood = inverse ? (columnItem as MatrixLikelihoodType) : (rowItem as MatrixLikelihoodType);
      const risk = riskMap.get(buildRiskKey(severity, likelihood));
      const cell = sheet.getCell(excelRow, colIndex + 2);

      const fillColor = normalizeHexColor(risk?.cell_color, `FF${DEFAULT_CELL_COLOR}`);
      const fontColor = normalizeHexColor(risk?.font_color, `FF${DEFAULT_FONT_COLOR}`);

      cell.value = risk?.name ?? 'Editar';
      cell.fill = {
        type: 'pattern',
        pattern: 'solid',
        fgColor: { argb: fillColor },
      };
      cell.font = { bold: true, color: { argb: fontColor } };
      cell.alignment = { vertical: 'middle', horizontal: 'center', wrapText: true };
      cell.border = buildBorder();
    });
  });

  const buffer = await workbook.xlsx.writeBuffer();
  const blob = new Blob([buffer], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  });

  const filename = `${sanitizeFileName(matrixName) || 'matriz'}.xlsx`;
  const link = document.createElement('a');
  const url = window.URL.createObjectURL(blob);
  link.href = url;
  link.setAttribute('download', filename);
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(url);
};
